/**
 * Coach-side Attendance Calendar.
 *
 * Month grid with per-day counters: "6 clients · 1 assessment"
 *   - Clients = scheduled regular training bookings
 *   - Assessments = bookings with source='assessment_request'
 *   - No-shows + late-cancels surface as small red/amber pills
 *
 * Click any day → drilldown panel showing every slot on that day with
 * each booked athlete + per-kid [Attended ✓] / [No-show ✗] / [Reset]
 * toggle. Plus a "Schedule a session" CTA that opens a quick-book modal
 * (athlete dropdown · slot dropdown · confirm).
 *
 * Default behavior: bookings auto-flip to "attended" via the existing
 * auto_checkin_sweep job. Coach only needs to flag no-shows manually.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
    ArrowLeft, ChevronLeft, ChevronRight, Loader2, Check, X, Plus,
    CalendarDays, RotateCcw, Trash2,
} from "lucide-react";
import AthleteNameLink from "@/components/coach/AthleteNameLink";
import OpsTabs from "@/components/coach/OpsTabs";
import { useAuth } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

function isoDate(year, month, day) {
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function fmtTime12(hhmm) {
    if (!hhmm) return "";
    const [h, m] = hhmm.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

export default function CoachAttendance() {
    const today = new Date();
    const [year, setYear] = useState(today.getUTCFullYear());
    const [month, setMonth] = useState(today.getUTCMonth() + 1); // 1-12
    const [monthData, setMonthData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [selectedDate, setSelectedDate] = useState(null);
    const [dayData, setDayData] = useState(null);
    const [dayBusy, setDayBusy] = useState(false);
    const [quickBookOpen, setQuickBookOpen] = useState(false);

    // Refs for the "today" cell + in-progress slot so we can scrollIntoView
    // once the month + day data settle.
    const todayCellRef = useRef(null);
    const liveSlotRef = useRef(null);
    const didAutoScrollMonth = useRef(false);
    const didAutoScrollDay = useRef(false); // eslint-disable-line no-unused-vars

    // Tick every 60s so the LIVE indicator + "now" highlight advance live.
    const [nowMs, setNowMs] = useState(() => Date.now());
    useEffect(() => {
        const id = window.setInterval(() => setNowMs(Date.now()), 60_000);
        return () => window.clearInterval(id);
    }, []);

    const todayIso = isoDate(today.getUTCFullYear(), today.getUTCMonth() + 1, today.getUTCDate());

    // Owner-only: ghost-cancellation cleanup (changed/ended recurring series
    // that left future CANCELLED rows cluttering the day view). (Sachin 2026-06)
    const { user } = useAuth();
    const isOwner = user?.role === "owner";
    const [ghostCount, setGhostCount] = useState(0);
    const [ghostBusy, setGhostBusy] = useState(false);

    const loadGhostCount = async () => {
        if (!isOwner) return;
        try {
            const r = await axios.get(
                `${API}/coach/recurring/orphaned-cancellations/count`,
                { withCredentials: true },
            );
            setGhostCount(r.data?.count || 0);
        } catch (_e) { /* non-critical */ }
    };

    useEffect(() => { loadGhostCount(); /* eslint-disable-next-line */ }, [isOwner]);

    const purgeGhosts = async () => {
        if (!window.confirm(
            `Purge ${ghostCount} ghost cancellation${ghostCount === 1 ? "" : "s"}?\n\n` +
            "These are future CANCELLED rows left behind by recurring schedules that were changed or ended. " +
            "Real single-session cancellations and all past records are left untouched.",
        )) return;
        setGhostBusy(true);
        try {
            const r = await axios.post(
                `${API}/coach/recurring/cleanup-orphaned-cancellations`,
                {},
                { withCredentials: true },
            );
            toast.success(`Removed ${r.data?.removed || 0} ghost cancellation rows`);
            setGhostCount(0);
            loadMonth(year, month);
            if (selectedDate) loadDay(selectedDate);
        } catch (e) {
            toast.error("Cleanup failed", { description: e?.response?.data?.detail });
        } finally {
            setGhostBusy(false);
        }
    };

    const loadMonth = async (y, m) => {
        setBusy(true);
        try {
            const r = await axios.get(
                `${API}/coach/attendance/month?year=${y}&month=${m}`,
                { withCredentials: true },
            );
            setMonthData(r.data);
        } catch (e) {
            toast.error("Couldn't load calendar", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    const loadDay = async (date) => {
        setDayBusy(true);
        try {
            const r = await axios.get(
                `${API}/coach/attendance/day?date=${date}`,
                { withCredentials: true },
            );
            setDayData(r.data);
        } catch (e) {
            toast.error("Couldn't load day", { description: e?.response?.data?.detail });
        } finally {
            setDayBusy(false);
        }
    };

    useEffect(() => { loadMonth(year, month); }, [year, month]);

    // Auto-select TODAY + load its drilldown on first page load so the coach
    // immediately sees who should be in. Also auto-scroll the page to the
    // today cell in case the month grid is below the fold.
    useEffect(() => {
        if (didAutoScrollMonth.current) return;
        if (!monthData) return;
        const inCurrentMonth = (
            year === today.getUTCFullYear() && month === (today.getUTCMonth() + 1)
        );
        if (!inCurrentMonth) return;
        didAutoScrollMonth.current = true;
        if (!selectedDate) {
            setSelectedDate(todayIso);
            loadDay(todayIso);
        }
        // 2026-06: no auto-scroll — pages must open at the top (Sachin).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [monthData]);

    // 2026-06: day-view auto-scroll to the in-progress slot removed — pages
    // must open at the top (Sachin). liveSlotRef retained for styling only.

    const goPrev = () => {
        if (month === 1) { setMonth(12); setYear(year - 1); } else { setMonth(month - 1); }
        setSelectedDate(null); setDayData(null);
    };
    const goNext = () => {
        if (month === 12) { setMonth(1); setYear(year + 1); } else { setMonth(month + 1); }
        setSelectedDate(null); setDayData(null);
    };
    const goToday = () => {
        setYear(today.getUTCFullYear());
        setMonth(today.getUTCMonth() + 1);
        const td = isoDate(today.getUTCFullYear(), today.getUTCMonth() + 1, today.getUTCDate());
        setSelectedDate(td); loadDay(td);
    };

    const calendar = useMemo(() => buildCalendar(year, month), [year, month]);

    const mark = async (bookingId, outcome, athleteName) => {
        try {
            const r = await axios.post(
                `${API}/coach/attendance/mark`,
                { booking_id: bookingId, outcome },
                { withCredentials: true },
            );
            if (outcome === "no_show" && r.data?.penalty?.penalised) {
                toast.error("No-show recorded", { description: r.data.message, duration: 7000 });
            } else if (outcome === "attended") {
                // LOUD feedback so coach can never miss a silent miss-deduct.
                // Mirrors the toastDeduction logic in Schedule.jsx.
                const consumed = r.data?.consumed || {};
                const name = athleteName || "Athlete";
                if (consumed.kind === "credit" || consumed.kind === "pack") {
                    const label = consumed.pack_label || "session pack";
                    const remaining = consumed.remaining;
                    const tail = remaining === 0
                        ? " · LAST SESSION — pack now empty"
                        : (typeof remaining === "number" ? ` · ${remaining} left` : "");
                    toast.success(`✓ ${name} marked attended`, {
                        description: `1 session deducted from ${label}${tail}`,
                        duration: 6000,
                        className: "border-emerald-600 bg-emerald-950/90 text-emerald-100",
                    });
                } else if (consumed.kind === "subscription") {
                    toast.success(`✓ ${name} marked attended`, {
                        description: "Subscription member — no pack deduction needed.",
                        duration: 5000,
                    });
                } else if (consumed.kind === "iou") {
                    toast.error(`⚠ ${name} marked attended — NO SESSION DEDUCTED`, {
                        description: "No active pack/credit. An IOU was opened — collect payment ASAP.",
                        duration: 10000,
                        className: "border-rose-600 bg-rose-950/95 text-rose-100",
                    });
                } else if (consumed.kind === "already_consumed") {
                    toast.success(`✓ ${name} re-marked attended`, {
                        description: "Session was already deducted earlier (no double-charge).",
                    });
                } else {
                    toast.success(r.data?.message || "Updated");
                }
            } else {
                toast.success(r.data?.message || "Updated");
            }
            await Promise.all([loadDay(selectedDate), loadMonth(year, month)]);
        } catch (e) {
            toast.error("Couldn't update", { description: e?.response?.data?.detail });
        }
    };

    // Delete an incorrect attendance record (Sachin 2026-08: "no matter how
    // it was logged, make sure it can be taken off"). Two-step confirm, then
    // ONE unified backend flow that accepts every record shape — scheduled,
    // walk-in, auto check-in, manual log, legacy statuses, deleted slots.
    // Refunds the deducted session (optional) + keeps the audit trail.
    const removeAttendance = async (row) => {
        const name = row.athlete_name || "this athlete";
        if (!window.confirm(`Delete this attendance record for ${name}?`)) return;
        const creditBack = window.confirm(
            "Credit the session back to their pack?\n\n" +
            "OK = credit it back (most common — entered by mistake).\n" +
            "Cancel = leave the pack as-is.",
        );
        try {
            const r = await axios.delete(
                `${API}/coach/attendance/record/${row.booking_id}?credit_back=${creditBack}`,
                { withCredentials: true },
            );
            const refund = r.data?.refunded_from;
            toast.success("Attendance deleted", {
                description: creditBack
                    ? (refund
                        ? `1 session credited back to ${refund.label} · ${refund.remaining} left.`
                        : "No matching deduction found — nothing to credit back.")
                    : "Pack untouched.",
            });
            await Promise.all([loadDay(selectedDate), loadMonth(year, month)]);
        } catch (e) {
            toast.error("Couldn't delete", { description: e?.response?.data?.detail || "Try again." });
        }
    };

    return (
        <div data-testid="coach-attendance" className="min-h-screen bg-black text-white">
            <div className="max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-16">
                <OpsTabs />
                <Link to="/coach" className="inline-flex items-center gap-1.5 text-zinc-300 hover:text-white text-xs tracking-[0.18em] uppercase">
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to coach
                </Link>
                <div className="mt-3 flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <h1 className="font-display text-4xl sm:text-5xl text-white tracking-tight">Attendance</h1>
                        <p className="text-zinc-400 text-sm mt-1.5">
                            Sessions are auto-marked attended unless flagged as a no-show.
                        </p>
                        {isOwner && ghostCount > 0 && (
                            <button
                                type="button"
                                data-testid="purge-ghost-cancellations"
                                onClick={purgeGhosts}
                                disabled={ghostBusy}
                                className="mt-3 inline-flex items-center gap-2 px-3 py-2 border border-amber-700/60 bg-amber-950/30 text-amber-200 hover:border-amber-500 hover:text-amber-100 text-[11px] tracking-[0.15em] uppercase transition-colors disabled:opacity-50 rounded-sm"
                                title="Remove future CANCELLED rows left behind by changed/ended recurring schedules"
                            >
                                {ghostBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                Purge {ghostCount} ghost cancellation{ghostCount === 1 ? "" : "s"}
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1.5">
                        <button
                            type="button"
                            data-testid="attendance-prev-month"
                            onClick={goPrev}
                            className="p-2 border border-zinc-800 hover:border-brand-red text-zinc-200 hover:text-brand-red"
                            aria-label="Previous month"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <div className="px-4 py-2 border border-zinc-800 text-[12px] tracking-[0.2em] uppercase min-w-[170px] text-center">
                            {MONTH_NAMES[month - 1]} {year}
                        </div>
                        <button
                            type="button"
                            data-testid="attendance-next-month"
                            onClick={goNext}
                            className="p-2 border border-zinc-800 hover:border-brand-red text-zinc-200 hover:text-brand-red"
                            aria-label="Next month"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={goToday}
                            data-testid="attendance-today"
                            className="ml-2 px-3 py-2 border border-zinc-800 hover:border-brand-red text-[11px] tracking-[0.2em] uppercase text-zinc-200 hover:text-brand-red"
                        >
                            Today
                        </button>
                    </div>
                </div>

                {busy ? (
                    <div className="mt-12 flex items-center justify-center text-zinc-500">
                        <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                ) : (
                    <div className="mt-8 grid lg:grid-cols-[2fr_1fr] gap-8">
                        {/* Month grid */}
                        <div data-testid="attendance-month-grid" className="border border-zinc-900 rounded-2xl bg-zinc-950/40 overflow-hidden">
                            <div className="grid grid-cols-7 border-b border-zinc-900 bg-zinc-950">
                                {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
                                    <div key={d} className="px-2 py-2 text-[10px] tracking-[0.25em] uppercase text-zinc-500 text-center">{d}</div>
                                ))}
                            </div>
                            <div className="grid grid-cols-7">
                                {calendar.map((cell, idx) => {
                                    if (!cell) {
                                        return <div key={`empty-${idx}`} className="aspect-[1.1] border-r border-b border-zinc-900 bg-zinc-950/30" />;
                                    }
                                    const date = isoDate(year, month, cell);
                                    const counters = monthData?.days?.[date] || { clients: 0, assessments: 0, no_shows: 0, late_cancels: 0 };
                                    const isToday = date === isoDate(today.getUTCFullYear(), today.getUTCMonth() + 1, today.getUTCDate());
                                    const isSelected = date === selectedDate;
                                    const hasAny = counters.clients > 0 || counters.assessments > 0 || counters.no_shows > 0 || counters.late_cancels > 0;
                                    return (
                                        <button
                                            key={date}
                                            ref={isToday ? todayCellRef : undefined}
                                            type="button"
                                            data-testid={`attendance-day-${date}`}
                                            data-today={isToday ? "1" : "0"}
                                            onClick={() => { setSelectedDate(date); loadDay(date); }}
                                            className={`relative aspect-[1.1] border-r border-b border-zinc-900 text-left p-2 transition-colors ${
                                                isSelected
                                                    ? "bg-brand-red/15 border-brand-red"
                                                    : isToday
                                                        ? "bg-zinc-900/60 ring-1 ring-brand-red/40"
                                                        : hasAny
                                                            ? "hover:bg-zinc-900/40"
                                                            : "hover:bg-zinc-900/20"
                                            }`}
                                        >
                                            {isToday && (
                                                <span
                                                    data-testid="attendance-today-pill"
                                                    className="absolute -top-1.5 right-1 inline-flex items-center text-[9px] font-bold tracking-[0.2em] uppercase px-1.5 py-0.5 bg-brand-red text-white rounded-sm shadow-md"
                                                >
                                                    ● Today
                                                </span>
                                            )}
                                            <div className={`text-sm font-medium ${isToday ? "text-brand-red" : "text-zinc-200"}`}>{cell}</div>
                                            {hasAny && (
                                                <div className="mt-1.5 space-y-0.5 text-[10px]">
                                                    {counters.clients > 0 && (
                                                        <div className="text-emerald-300">{counters.clients} client{counters.clients !== 1 ? "s" : ""}</div>
                                                    )}
                                                    {counters.assessments > 0 && (
                                                        <div className="text-brand-red">{counters.assessments} assess{counters.assessments !== 1 ? "es" : ""}</div>
                                                    )}
                                                    {counters.no_shows > 0 && (
                                                        <div className="text-rose-400">{counters.no_shows} no-show{counters.no_shows !== 1 ? "s" : ""}</div>
                                                    )}
                                                    {counters.late_cancels > 0 && (
                                                        <div className="text-amber-400">{counters.late_cancels} late-cancel{counters.late_cancels !== 1 ? "s" : ""}</div>
                                                    )}
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Day drilldown */}
                        <div data-testid="attendance-day-panel" className="border border-zinc-900 rounded-2xl bg-zinc-950/40 p-5 self-start sticky top-24">
                            <div className="flex items-center justify-between gap-2 mb-3">
                                <h2 className="text-[11px] tracking-[0.25em] uppercase text-zinc-300 inline-flex items-center gap-2">
                                    <CalendarDays className="w-3.5 h-3.5" />
                                    {selectedDate
                                        ? new Date(`${selectedDate}T00:00:00Z`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" })
                                        : "Pick a day"}
                                </h2>
                                {selectedDate && (
                                    <button
                                        type="button"
                                        data-testid="attendance-quick-book-open"
                                        onClick={() => setQuickBookOpen(true)}
                                        className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase px-2.5 py-1.5 border border-brand-red text-brand-red hover:bg-brand-red hover:text-white transition-colors"
                                    >
                                        <Plus className="w-3 h-3" /> Schedule
                                    </button>
                                )}
                            </div>
                            {!selectedDate && (
                                <p className="text-xs text-zinc-500">Click a day on the calendar to see who's scheduled.</p>
                            )}
                            {selectedDate && (
                                dayBusy ? (
                                    <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
                                ) : (
                                    <DayView
                                        day={dayData}
                                        onMark={mark}
                                        onDelete={removeAttendance}
                                        nowMs={nowMs}
                                        isToday={selectedDate === todayIso}
                                        liveSlotRef={liveSlotRef}
                                    />
                                )
                            )}
                        </div>
                    </div>
                )}

                {quickBookOpen && selectedDate && (
                    <QuickBookModal
                        date={selectedDate}
                        slots={dayData?.slots || []}
                        onClose={() => setQuickBookOpen(false)}
                        onBooked={async () => {
                            setQuickBookOpen(false);
                            await Promise.all([loadDay(selectedDate), loadMonth(year, month)]);
                        }}
                    />
                )}
            </div>
        </div>
    );
}


function DayView({ day, onMark, onDelete, nowMs, isToday, liveSlotRef }) {
    const manualLogs = day?.manual_logs || [];
    if (!day || (day.slots.length === 0 && manualLogs.length === 0)) {
        return <p className="text-xs text-zinc-500">No sessions on this day. Tap <strong>Schedule</strong> to add one.</p>;
    }
    return (
        <div className="space-y-4">
            {day.slots.map((s) => {
                // Mark a slot "live" only on today's view — past/future dates
                // never highlight (avoids confusing past-day reviews).
                let live = false;
                if (isToday && s.start_time && nowMs) {
                    const [hh, mm] = s.start_time.split(":").map(Number);
                    const t = new Date(nowMs);
                    const start = new Date(t.getFullYear(), t.getMonth(), t.getDate(), hh, mm, 0, 0).getTime();
                    const end = start + (s.duration_minutes || 60) * 60_000;
                    live = nowMs >= start && nowMs < end;
                }
                return (
                    <div
                        key={s.slot_id}
                        ref={live ? liveSlotRef : undefined}
                        data-testid={`attendance-slot-${s.slot_id}`}
                        data-live={live ? "1" : "0"}
                        className={`border rounded ${
                            live
                                ? "border-brand-red ring-1 ring-brand-red/40 shadow-[0_0_24px_-6px] shadow-brand-red/60"
                                : "border-zinc-900"
                        }`}
                    >
                        <div className={`px-3 py-2 border-b flex items-center justify-between ${
                            live ? "border-brand-red/40 bg-brand-red/10" : "border-zinc-900 bg-zinc-950"
                        }`}>
                            <div className="text-sm text-white inline-flex items-center gap-2">
                                {live && (
                                    <span
                                        data-testid={`attendance-now-pill-${s.slot_id}`}
                                        className="inline-flex items-center text-[9px] font-bold tracking-[0.2em] uppercase px-1.5 py-0.5 bg-brand-red text-white rounded-sm animate-pulse"
                                    >
                                        ● Now
                                    </span>
                                )}
                                {fmtTime12(s.start_time)} <span className="text-zinc-500">· {s.duration_minutes}min</span>
                            </div>
                            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                                {s.is_assessment_slot ? "Assessment" : (s.label || s.category || "Session")}
                            </div>
                        </div>
                        <div className="divide-y divide-zinc-900">
                            {s.attendees.length === 0 ? (
                                <div className="px-3 py-2 text-[11px] text-zinc-500">Empty slot.</div>
                            ) : s.attendees.map((a) => (
                                <AttendeeRow key={a.booking_id} a={a} onMark={onMark} onDelete={onDelete} />
                            ))}
                        </div>
                    </div>
                );
            })}
            {manualLogs.length > 0 && (
                <div data-testid="attendance-manual-logs" className="border border-zinc-900 rounded">
                    <div className="px-3 py-2 border-b border-zinc-900 bg-zinc-950 flex items-center justify-between">
                        <div className="text-sm text-white">Manual logs &amp; off-schedule check-ins</div>
                        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                            {manualLogs.length} logged
                        </div>
                    </div>
                    <div className="divide-y divide-zinc-900">
                        {manualLogs.map((m) => (
                            <div
                                key={m.booking_id}
                                data-testid={`attendance-manual-${m.booking_id}`}
                                className="px-3 py-2 flex items-center justify-between gap-3"
                            >
                                <div className="min-w-0">
                                    <div className="text-sm text-white truncate">
                                        <AthleteNameLink
                                            athleteId={m.athlete_id}
                                            name={m.athlete_name}
                                            testid={`attendance-manual-name-${m.booking_id}`}
                                            className="text-white hover:text-brand-red underline-offset-2 hover:underline transition-colors"
                                        />
                                    </div>
                                    <div className={`text-[10px] tracking-wider uppercase mt-0.5 ${m.slot_removed ? "text-amber-300" : "text-emerald-300"}`}>
                                        Attended · {m.slot_removed ? "original slot removed" : m.is_walk_in ? "walk-in" : "manual log"}
                                        {m.start_time ? ` · ${fmtTime12(m.start_time)}` : ""}
                                    </div>
                                    {m.note && (
                                        <div className="text-[11px] text-zinc-500 mt-0.5 truncate">“{m.note}”</div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    data-testid={`attendance-manual-delete-${m.booking_id}`}
                                    onClick={() => onDelete(m)}
                                    className="p-1.5 border border-zinc-800 hover:border-rose-500 text-zinc-500 hover:text-rose-400 transition-colors flex-shrink-0"
                                    title="Delete this attendance record (optional session refund)"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}


function AttendeeRow({ a, onMark, onDelete }) {
    const statusMeta = {
        booked: { label: "Scheduled", color: "text-zinc-300" },
        checked_in: { label: a.auto_checked_in ? "Attended · auto" : "Attended", color: "text-emerald-300" },
        no_show: { label: "No-show", color: "text-rose-400" },
        cancelled: { label: a.late_cancel ? "Late-cancel" : "Cancelled", color: a.late_cancel ? "text-amber-400" : "text-zinc-500" },
    }[a.status] || { label: a.status, color: "text-zinc-400" };
    return (
        <div data-testid={`attendance-attendee-${a.booking_id}`} className="px-3 py-2 flex items-center justify-between gap-3">
            <div className="min-w-0">
                <div className="text-sm text-white truncate">
                    <AthleteNameLink
                        athleteId={a.athlete_id}
                        name={a.athlete_name}
                        testid={`attendance-name-${a.booking_id}`}
                        className="text-white hover:text-brand-red underline-offset-2 hover:underline transition-colors"
                    />
                    {a.is_assessment && (
                        <span className="ml-2 text-[9px] tracking-wider uppercase px-1.5 py-0.5 border border-brand-red/60 text-brand-red">A</span>
                    )}
                </div>
                <div className={`text-[10px] tracking-wider uppercase mt-0.5 ${statusMeta.color}`}>{statusMeta.label}</div>
            </div>
            {a.status === "cancelled" ? null : (
                <div className="flex items-center gap-1.5">
                    {a.status !== "checked_in" && (
                        <button
                            type="button"
                            data-testid={`attendance-mark-attended-${a.booking_id}`}
                            onClick={() => onMark(a.booking_id, "attended", a.athlete_name)}
                            className="p-1.5 border border-zinc-800 hover:border-emerald-500 text-emerald-400 transition-colors"
                            title="Mark attended"
                        >
                            <Check className="w-3.5 h-3.5" />
                        </button>
                    )}
                    {a.status !== "no_show" && (
                        <button
                            type="button"
                            data-testid={`attendance-mark-noshow-${a.booking_id}`}
                            onClick={() => onMark(a.booking_id, "no_show", a.athlete_name)}
                            className="p-1.5 border border-zinc-800 hover:border-rose-500 text-rose-400 transition-colors"
                            title="Mark no-show"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                    {(a.status === "checked_in" || a.status === "no_show") && (
                        <button
                            type="button"
                            data-testid={`attendance-mark-reset-${a.booking_id}`}
                            onClick={() => onMark(a.booking_id, "reset")}
                            className="p-1.5 border border-zinc-800 hover:border-zinc-600 text-zinc-400 transition-colors"
                            title="Reset to scheduled (refunds the session if one was deducted)"
                        >
                            <RotateCcw className="w-3.5 h-3.5" />
                        </button>
                    )}
                    {(a.status === "checked_in" || a.status === "attended" || a.checked_in_at || a.marked_attended_at) && (
                        <button
                            type="button"
                            data-testid={`attendance-delete-${a.booking_id}`}
                            onClick={() => onDelete(a)}
                            className="p-1.5 border border-zinc-800 hover:border-rose-500 text-zinc-500 hover:text-rose-400 transition-colors"
                            title="Delete this check-in entirely (optional session refund)"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}


function QuickBookModal({ date, slots, onClose, onBooked }) {
    const [athletes, setAthletes] = useState([]);
    const [athleteId, setAthleteId] = useState("");
    const [slotId, setSlotId] = useState("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(`${API}/coach/attendance/athletes`, { withCredentials: true });
                setAthletes(r.data || []);
            } catch (e) {
                toast.error("Couldn't load athletes", { description: e?.response?.data?.detail });
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const availableSlots = slots.filter((s) => s.attendees.length < s.capacity);

    const confirm = async () => {
        if (!athleteId || !slotId) {
            toast.error("Pick both athlete and time");
            return;
        }
        setBusy(true);
        try {
            const r = await axios.post(
                `${API}/coach/attendance/quick-book`,
                { athlete_id: athleteId, date, slot_id: slotId },
                { withCredentials: true },
            );
            toast.success(r.data?.message || "Booked");
            onBooked();
        } catch (e) {
            toast.error("Couldn't book", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="quick-book-modal"
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
            onClick={onClose}
        >
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
                <div>
                    <h2 className="font-display text-2xl text-white">Schedule a session</h2>
                    <p className="text-xs text-zinc-400 mt-1">
                        {new Date(`${date}T00:00:00Z`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" })}
                    </p>
                </div>
                {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
                ) : (
                    <>
                        <div>
                            <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400">Athlete</label>
                            <select
                                data-testid="quick-book-athlete"
                                value={athleteId}
                                onChange={(e) => setAthleteId(e.target.value)}
                                className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-white focus:border-brand-red outline-none"
                            >
                                <option value="">— Pick athlete —</option>
                                {athletes.map((a) => (
                                    <option key={a.athlete_id} value={a.athlete_id}>
                                        {a.name}{a.parent_name ? ` (${a.parent_name})` : ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400">Time</label>
                            <select
                                data-testid="quick-book-slot"
                                value={slotId}
                                onChange={(e) => setSlotId(e.target.value)}
                                className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-white focus:border-brand-red outline-none"
                            >
                                <option value="">— Pick time —</option>
                                {availableSlots.map((s) => (
                                    <option key={s.slot_id} value={s.slot_id}>
                                        {fmtTime12(s.start_time)} · {s.label || s.category || "Session"} ({s.attendees.length}/{s.capacity})
                                    </option>
                                ))}
                            </select>
                            {availableSlots.length === 0 && (
                                <p className="text-[11px] text-amber-300 mt-1">
                                    No open slots on this day. Create a slot first from /coach/schedule.
                                </p>
                            )}
                        </div>
                        <div className="flex items-center justify-end gap-2 pt-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="text-[11px] tracking-[0.18em] uppercase px-3 py-2 text-zinc-400 hover:text-white"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                data-testid="quick-book-confirm"
                                onClick={confirm}
                                disabled={busy || !athleteId || !slotId}
                                className="text-[11px] tracking-[0.18em] uppercase px-4 py-2 border border-brand-red bg-brand-red text-white hover:bg-brand-red/80 transition-colors disabled:opacity-40"
                            >
                                {busy ? "Booking…" : "Confirm booking"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}


// Build a Sun-first calendar grid for the given month.
// Returns an array of length 42 with day-numbers or null (for empty cells).
function buildCalendar(year, month) {
    const first = new Date(Date.UTC(year, month - 1, 1));
    const startDow = first.getUTCDay(); // 0 = Sun
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
}
